/**
 * useAdminCategory Hook
 *
 * Fetches a single category with all categories list for admin using URQL query.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const AdminCategoryDocument = `
  query AdminCategory($id: ID!) {
    category(id: $id) {
      id code name description color icon
      parent { id name }
      sortOrder isActive eventCount
    }
    categories(includeChildren: true) { id name }
  }
`;

const CategoriesListDocument = `
  query CategoriesList {
    categories(includeChildren: true) { id name }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface AdminCategoryDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  parent: { id: string; name: string } | null;
  sortOrder: number;
  isActive: boolean;
  eventCount: number;
}

export interface CategoryOption {
  id: string;
  name: string;
}

interface UseAdminCategoryOptions {
  pause?: boolean;
}

interface UseAdminCategoryResult {
  category: AdminCategoryDetail | null;
  allCategories: CategoryOption[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseCategoriesListResult {
  categories: CategoryOption[];
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch a single category and all categories for parent selection.
 */
export function useAdminCategory(
  categoryId: string | undefined,
  options: UseAdminCategoryOptions = {}
): UseAdminCategoryResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    category: AdminCategoryDetail | null;
    categories: CategoryOption[];
  }>({
    query: AdminCategoryDocument,
    variables: { id: categoryId },
    pause: pause || !categoryId,
  });

  return {
    category: result.data?.category ?? null,
    allCategories: result.data?.categories ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

/**
 * Fetch only categories list (for new category creation).
 */
export function useCategoriesList(
  options: UseAdminCategoryOptions = {}
): UseCategoriesListResult {
  const { pause = false } = options;

  const [result] = useQuery<{ categories: CategoryOption[] }>({
    query: CategoriesListDocument,
    pause,
  });

  return {
    categories: result.data?.categories ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
